import { Icon } from '@/features/common/Icon';
import { formatFileSize } from '@/features/common/richText';
import { splitEventFiles } from '@/features/publicsite/events/eventMedia';
import { mediaApi } from '@/services/mediaApi';
import { publicEvents } from '@/i18n/dictionaries/publicEvents';
import { useTranslation } from '@/i18n/useTranslation';
import type { EventFilesProps } from '@/features/publicsite/types/events/EventFilesProps';

/**
 * One of the event card's two file sections — the relevant documents, or the post-event media archive
 * (tor.md §6.11) — each file by its own name and size. They were one list under one heading, so the
 * programme sent before the event and the slides from after it read as the same kind of thing.
 *
 * A recording plays on the page. It was a link that downloaded 50 MB to find out what it was, which is
 * not what „expunerea arhivei media (video, prezentări)” describes. The name and a download link stay
 * under the player: a visitor on a slow connection, or one who needs the file itself, still has it.
 */
export function EventFiles({ heading, files }: EventFilesProps) {
  const { t, language } = useTranslation(publicEvents);
  const { videos, downloads } = splitEventFiles(files);
  return (
    <section className="event-detail__section">
      <h2 className="event-detail__section-heading">{heading}</h2>
      {videos.map((v) => (
        <figure className="event-files__video" key={v.id}>
          {/* preload="metadata": the page must not start fetching every recording it lists. */}
          <video className="event-files__player" controls preload="metadata">
            <source src={mediaApi.downloadUrl(v.id)} type={v.mimeType} />
            {t('videoUnsupported')}
          </video>
          <figcaption className="event-files__caption">
            <span className="event-files__caption-name">{v.name}</span>
            <span className="event-files__caption-size">
              {formatFileSize(v.sizeBytes, language)}
            </span>
            <a
              href={mediaApi.downloadUrl(v.id)}
              aria-label={t('downloadFileAria', { name: v.name })}
            >
              {t('downloadFile')}
            </a>
          </figcaption>
        </figure>
      ))}
      {downloads.length > 0 && (
        <ul className="attachment-list">
          {downloads.map((d) => (
            <li className="attachment-row" key={d.id}>
              <Icon name="attachment" className="attachment-row__icon" />
              <a className="attachment-row__name" href={mediaApi.downloadUrl(d.id)}>
                {d.name}
              </a>
              <span className="attachment-row__size">{formatFileSize(d.sizeBytes, language)}</span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
